import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../../services/configUrls";

const SocketContext = createContext(null);

export const SocketProvide = ({ children }) => {
  const socketRef = useRef(null);
  const [lastMessage, setLastMessage] = useState(null);
  const [userId, setUserId] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  
  // Refs for cleanup
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const shouldReconnectRef = useRef(true);

  // Fetch user_id from API using redirect tokens in URL or stored token
  useEffect(() => {
    const fetchUserIdFromAPI = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const accessToken = urlParams.get("accessToken");
        const refreshToken = urlParams.get("refreshToken");
        const email = urlParams.get("email");

        let finalAccessToken = accessToken;

        // If URL has tokens → parse them via backend API
        if (accessToken && refreshToken && email) {
          console.log("🔄 Parsing tokens from redirect URL...");
          
          const data = {
            redirect_url: `${window.location.origin}?accessToken=${accessToken}&refreshToken=${refreshToken}&email=${email}`,
          };

          const bearerToken = localStorage.getItem("bearerToken");

          const response = await axios.post(
            `${BASE_URL}/staffmetting/fetch_token`,
            data,
            {
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${bearerToken}`
              }
            }
          );

          if (response.data?.user_id) {
            console.log("✅ User ID received from API:", response.data.user_id);
            setUserId(response.data.user_id);

            // Store tokens for future use
            localStorage.setItem("accessToken", accessToken);
            localStorage.setItem("refreshToken", refreshToken);
            localStorage.setItem("userEmail", email);

            // Clean URL
            const newUrl =
              window.location.protocol +
              "//" +
              window.location.host +
              window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
            return;
          }
        }

        // If no tokens in URL, try localStorage
        if (!finalAccessToken) {
          finalAccessToken = localStorage.getItem("accessToken");
        }

        // If still no token, abort
        if (!finalAccessToken) {
          console.warn("⚠️ No access token found in URL or localStorage");
          return;
        }

        // Call the same API with token from localStorage to get user_id
        console.log("🔄 Fetching user ID using stored token...");
        
        const storedRefreshToken = localStorage.getItem("refreshToken");
        const storedEmail = localStorage.getItem("userEmail");

        const data = {
          redirect_url: `${window.location.origin}?accessToken=${finalAccessToken}${storedRefreshToken ? `&refreshToken=${storedRefreshToken}` : ''}${storedEmail ? `&email=${storedEmail}` : ''}`
        };
        console.log("data===>", data);

        const response = await axios.post(
          `${BASE_URL}/staffmetting/fetch_token`,
          data,
          {
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${localStorage.getItem("accessToken")}`
            }
          }
        );

        if (response.data?.user_id) {
          console.log("✅ User ID fetched successfully:", response.data.user_id);
          setUserId(response.data.user_id);
        } else {
          console.warn("⚠️ No user_id returned from API.");
        }
      } catch (error) {
        console.error("❌ Error fetching user ID from API:", error);
      }
    };

    fetchUserIdFromAPI();
  }, []);

  // WebSocket connection effect with ping-pong and auto-reconnect
  useEffect(() => {
    if (!userId) return;

    const connect = () => {
      // Prevent duplicate connections
      if (socketRef.current?.readyState === WebSocket.OPEN || 
          socketRef.current?.readyState === WebSocket.CONNECTING) {
        console.log("⚠️ WebSocket already connected or connecting");
        return;
      }

      const socketUrl = `wss://erpapi.eduskillsfoundation.org/staffmetting/ws/staff/${userId}`;
      console.log("🔗 Connecting WebSocket:", socketUrl);

      setConnectionStatus("connecting");
      socketRef.current = new WebSocket(socketUrl);

      socketRef.current.onopen = () => {
        console.log("✅ WebSocket connected successfully");
        setConnectionStatus("connected");

        // Start ping-pong heartbeat every 30 seconds
        pingIntervalRef.current = setInterval(() => {
          if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify({ type: "ping" }));
            console.log("📤 Ping sent");
          }
        }, 30000); // 30 seconds
      };

      socketRef.current.onmessage = (event) => {
        console.log("📩 Message received:", event.data);
        
        // Handle pong response if server sends it
        try {
          const data = JSON.parse(event.data);
          if (data.type === "pong") {
            console.log("📥 Pong received");
            return;
          }
        } catch (e) {
          // Not JSON or not a pong, treat as regular message
        }
        
        setLastMessage(event.data);
      };

      socketRef.current.onerror = (error) => {
        console.error("⚠️ WebSocket error:", error);
        setConnectionStatus("error");
      };

      socketRef.current.onclose = (event) => {
        console.warn("❌ WebSocket disconnected:", event.code, event.reason);
        
        // Cleanup ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        
        socketRef.current = null;
        setConnectionStatus("disconnected");

        // Auto-reconnect after 3 seconds if component is still mounted
        if (shouldReconnectRef.current) {
          console.log("🔄 Reconnecting in 3 seconds...");
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 3000);
        }
      };
    };

    connect();

    // Cleanup on unmount
    return () => {
      console.log("🧹 Cleaning up WebSocket connection");
      shouldReconnectRef.current = false;
      
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [userId]);

  return (
    <SocketContext.Provider
      value={{
        socket: socketRef.current,
        lastMessage,
        userId,
        connectionStatus,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);
